import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import { Modal, Spinner, toast } from '../ui'
import type { Job, Media, ProjectData, Take } from '../types'
import { cls, plural } from '../util'
import { lookCams, lookLabel, profileLabel, PROFILES } from './look'

/*
  Everything the machines do after footage is added, in plain words with real numbers:
  read the files, line the cameras and sound up, transcribe, make the web video. The header
  chip opens the full list; the Cameras & sound dialog shows what ended up where and lets
  the team fix a camera letter.
*/

export type ProxyProgress = { step: string; pct?: number; eta?: number | null; ahead?: number | null }
export type MediaStatus = { id: string; name: string; status: string; progress?: ProxyProgress }

/** "about 4 min left" from seconds. */
export function etaText(s: number | null | undefined): string {
  if (s == null || !isFinite(s)) return ''
  if (s < 45) return 'less than a minute left'
  const m = Math.round(s / 60)
  if (m < 60) return `about ${m} min left`
  const h = Math.floor(m / 60)
  return `about ${h} hr${m % 60 >= 5 ? ` ${m % 60} min` : ''} left`
}

/** What the web-video maker is doing for one clip, as one line. */
export function proxyLine(p: ProxyProgress | undefined): { text: string; pct: number | null; eta: string } {
  if (!p) return { text: 'Waiting to start', pct: null, eta: '' }
  if (p.step === 'queued') return { text: p.ahead ? `Waiting: ${plural(p.ahead, 'video')} ahead of it` : 'Starting soon', pct: null, eta: '' }
  const label = p.step === 'copy' ? 'Step 1 of 2: getting the video ready to stream' : p.step === 'convert' ? 'Step 1 of 2: making a web-friendly copy' : p.step === 'finish' ? 'Step 1 of 2: finishing up' : 'Step 2 of 2: uploading it for playback'
  return { text: label, pct: p.pct ?? null, eta: etaText(p.eta) }
}

/** Polls where every clip's web video stands while any is still being made. Team only. */
export function useMediaStatus(projectId: string, on: boolean, mediaCount: number) {
  const [list, setList] = useState<MediaStatus[] | null>(null)
  useEffect(() => {
    if (!on) return
    let live = true
    let timer: ReturnType<typeof setTimeout>
    const load = async () => {
      try {
        const r = await api.get<{ media: MediaStatus[] }>(`p/${projectId}/media-status`)
        if (!live) return
        setList(r.media)
        const busy = r.media.some((m) => m.status === 'pending')
        timer = setTimeout(load, busy ? 6000 : 60000)
      } catch {
        if (live) timer = setTimeout(load, 20000)
      }
    }
    load()
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [projectId, on, mediaCount])
  return list
}

type Step = { key: string; title: string; state: 'done' | 'active' | 'waiting' | 'problem'; detail: string; pct: number | null; eta: string; items?: { name: string; text: string; pct: number | null; eta: string }[] }

function jobEta(j: Job, serverTime: number): number | null {
  if (j.status !== 'running' || !j.claimed_at || j.progress < 0.03) return null
  const el = (serverTime - j.claimed_at) / 1000
  return (el / j.progress) * (1 - j.progress)
}

export function buildSteps(data: ProjectData, mediaStatus: MediaStatus[] | null): Step[] {
  const steps: Step[] = []
  const t = data.serverTime
  const ingest = data.jobs.filter((j) => j.type === 'ingest')
  const openIngest = ingest.find((j) => j.status === 'running') || ingest.find((j) => j.status === 'queued')
  const failedIngest = ingest.filter((j) => j.status === 'failed').pop()
  if (openIngest || failedIngest) {
    const running = openIngest?.status === 'running'
    const reading = running && openIngest!.progress < 0.5
    steps.push({
      key: 'read',
      title: 'Reading the files',
      state: failedIngest && !openIngest ? 'problem' : !openIngest ? 'done' : reading ? 'active' : running ? 'done' : 'waiting',
      detail: failedIngest && !openIngest ? `Couldn't finish: ${failedIngest.error || 'unknown error'}` : reading ? openIngest!.note || 'Checking timecode, length and sound on each file' : running ? 'Done' : 'Waiting for a machine to pick it up',
      pct: reading ? openIngest!.progress / 0.5 : null,
      eta: reading ? etaText(jobEta(openIngest!, t)) : '',
    })
    steps.push({
      key: 'sync',
      title: 'Lining up cameras and sound',
      state: !openIngest ? (failedIngest ? 'problem' : 'done') : running && !reading ? 'active' : 'waiting',
      detail: running && !reading ? openIngest!.note || 'Matching timecode, then sound waveforms' : 'First by timecode, then by matching the sound waveforms so every camera lands on the same frame',
      pct: running && !reading ? (openIngest!.progress - 0.5) / 0.5 : null,
      eta: running && !reading ? etaText(jobEta(openIngest!, t)) : '',
    })
  }
  const tx = data.jobs.filter((j) => j.type === 'transcribe')
  const takeById = new Map(data.takes.map((k) => [k.id, k]))
  const openTx = tx.filter((j) => j.status === 'running' || j.status === 'queued')
  const needTx = data.takes.filter((k) => k.status === 'queued' || k.status === 'processing')
  if (openTx.length || needTx.length || tx.length) {
    const done = data.takes.filter((k) => k.status === 'ready').length
    const total = data.takes.filter((k) => k.status !== 'none').length
    const running = openTx.filter((j) => j.status === 'running')
    // Speed so far (seconds of interview per second of work) predicts the rest of the queue.
    let rate = 0
    for (const j of running) {
      const k = takeById.get(j.target_id || '')
      if (k && j.claimed_at && j.progress > 0.03) rate += (k.duration * j.progress) / Math.max(1, (t - j.claimed_at) / 1000)
    }
    const left = openTx.reduce((n, j) => n + (takeById.get(j.target_id || '')?.duration || 0) * (1 - (j.status === 'running' ? j.progress : 0)), 0)
    const failed = data.takes.filter((k) => k.status === 'failed')
    steps.push({
      key: 'tx',
      title: 'Transcribing',
      state: openTx.length ? 'active' : failed.length ? 'problem' : 'done',
      detail: openTx.length ? `${done} of ${total} done${running.length ? '' : ', waiting for a machine'}` : failed.length ? `${plural(failed.length, 'clip')} failed: ${failed[0].error || 'unknown error'}` : `All ${plural(total, 'clip')} done`,
      pct: total ? (done + running.reduce((n, j) => n + j.progress, 0)) / total : null,
      eta: rate > 0 ? etaText(left / rate) : '',
      items: running.map((j) => ({ name: takeById.get(j.target_id || '')?.name || 'clip', text: 'transcribing', pct: j.progress, eta: etaText(jobEta(j, t)) })),
    })
  }
  if (mediaStatus?.length) {
    const pending = mediaStatus.filter((m) => m.status === 'pending')
    const failed = mediaStatus.filter((m) => m.status === 'failed')
    const ready = mediaStatus.length - pending.length - failed.length
    const active = pending.find((m) => m.progress && m.progress.step !== 'queued')
    steps.push({
      key: 'video',
      title: 'Preparing video for playback',
      state: pending.length ? 'active' : failed.length ? 'problem' : 'done',
      detail: pending.length ? `${ready} of ${mediaStatus.length} ready. You can read and mark the transcript meanwhile.` : failed.length ? `${plural(failed.length, 'clip')} can't play in a browser (${failed[0].name})` : `All ${plural(mediaStatus.length, 'video')} ready`,
      pct: null,
      eta: active ? proxyLine(active.progress).eta : '',
      items: pending.map((m) => {
        const l = proxyLine(m.progress)
        return { name: m.name, text: l.text, pct: l.pct, eta: l.eta }
      }),
    })
  }
  return steps
}

export function stepsBusy(steps: Step[]) {
  return steps.some((s) => s.state === 'active' || s.state === 'waiting')
}

/** The header chip: the one step in progress, with its numbers. */
export function ProcessingChip({ steps, noMachine, onOpen }: { steps: Step[]; noMachine: boolean; onOpen: () => void }) {
  const cur = steps.find((s) => s.state === 'active') || steps.find((s) => s.state === 'waiting')
  const problem = steps.find((s) => s.state === 'problem')
  if (!cur && !problem) return null
  if (noMachine && cur && cur.key !== 'video')
    return <button className="chip chip-warn chip-btn" onClick={onOpen} title="Jobs are waiting but no transcription machine has checked in for 10 minutes.">!<span className="chip-text"> waiting for a transcription machine</span></button>
  if (!cur) return <button className="chip chip-warn chip-btn" onClick={onOpen}>!<span className="chip-text"> {problem!.title.toLowerCase()}: needs a look</span></button>
  const pct = cur.pct != null ? ` ${Math.round(cur.pct * 100)}%` : ''
  return (
    <button className="chip chip-btn" onClick={onOpen} title="See every step">
      <Spinner small /><span className="chip-text">{cur.title.toLowerCase()}</span>{pct}<span className="chip-text">{cur.eta ? ` · ${cur.eta}` : ''}</span>
    </button>
  )
}

export function ProcessingPanel({ steps, noMachine, onClose, onCameras }: { steps: Step[]; noMachine: boolean; onClose: () => void; onCameras: () => void }) {
  return (
    <Modal title="What's happening" onClose={onClose}>
      <p className="muted small proc-intro">After footage is added, a machine works through these steps on its own. This updates live, and you can close it any time.</p>
      {noMachine && <p className="proc-warn small">No transcription machine has checked in for 10 minutes, so work is waiting. Wake Will's laptop or check the cloud runner.</p>}
      {steps.length === 0 && <p className="muted">Nothing is running.</p>}
      <ol className="proc-steps">
        {steps.map((s) => (
          <li key={s.key} className={cls('proc-step', s.state)}>
            <span className="proc-dot">{s.state === 'active' ? <Spinner small /> : s.state === 'done' ? '✓' : s.state === 'problem' ? '!' : ''}</span>
            <div className="proc-body">
              <div className="proc-title"><b>{s.title}</b>{s.eta && <span className="muted small">{s.eta}</span>}</div>
              <div className="muted small">{s.detail}</div>
              {s.state === 'active' && s.pct != null && <div className="bar"><div style={{ width: `${Math.max(2, Math.min(100, s.pct * 100))}%` }} /></div>}
              {s.items?.map((it, i) => (
                <div key={i} className="proc-item small">
                  <div className="proc-title"><span className="ellipsis">{it.name}</span><span className="muted">{it.pct != null ? `${Math.round(it.pct * 100)}%` : ''}{it.eta ? ` · ${it.eta}` : ''}</span></div>
                  <div className="muted">{it.text}</div>
                  {it.pct != null && <div className="bar"><div style={{ width: `${Math.max(2, it.pct * 100)}%` }} /></div>}
                </div>
              ))}
            </div>
          </li>
        ))}
      </ol>
      <div className="form-actions">
        <button className="btn btn-ghost" onClick={onCameras}>See cameras & sound</button>
        <button className="btn btn-primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  )
}

const SYNC_TEXT: Record<string, string> = {
  sound: 'lined up by sound',
  timecode: 'lined up by timecode',
  single: 'only camera',
  manual: 'set by hand',
}

/** Every interview, which file is which camera, where the sound comes from, and how it was lined up. */
export function CamerasDialog({ projectId, data, media, onClose, onChanged, onAdd, setLook }: {
  projectId: string; data: ProjectData; media: Map<string, Media>; onClose: () => void; onChanged: () => void; onAdd: () => void
  setLook: (change: { look?: string; client?: boolean; cams?: Record<string, string> }) => Promise<unknown>
}) {
  const [busy, setBusy] = useState('')
  const takes = data.takes.filter((t) => t.kind === 'sync' || t.angles.length > 1)
  const other = data.takes.filter((t) => !takes.includes(t))
  const setLabel = async (take: Take, mid: string, label: string) => {
    setBusy(mid)
    try {
      await api.patch(`p/${projectId}/takes/${take.id}/angles/${mid}`, { label })
      onChanged()
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not change the camera', 'err')
    } finally {
      setBusy('')
    }
  }
  const unplaced = data.media.filter((m) => !data.takes.some((t) => t.angles.some((a) => a.media_id === m.id) || t.audio_media_id === m.id))
  return (
    <Modal title="Cameras & sound" onClose={onClose} wide>
      <div className="explain small">
        <b>How footage gets matched</b>
        <p>Files that were rolling at the same time become one interview, with each file as a camera angle. We place them by timecode first, then compare the sound waveforms so every camera lands on the same frame (that also catches cameras whose timecode was off or missing).</p>
        <p>The letter is which camera it is. We read it from the file or folder name (A001_C003, "CamB", a "B cam" folder). If the name doesn't say, we guess, and you can change it here.</p>
      </div>
      <LookSection data={data} media={media} setLook={setLook} />
      <div className="cams-list">
        {takes.map((t) => {
          const audio = t.audio_media_id ? media.get(t.audio_media_id) : null
          return (
            <div key={t.id} className="cams-take">
              <div className="cams-take-head">
                <b className="ellipsis">{t.interview || t.name}</b>
                <span className="muted small">{t.interview && t.interview !== t.name ? `${t.name} · ` : ''}{plural(t.angles.filter((a) => a.label !== 'Audio').length, 'camera')}</span>
              </div>
              {t.angles.filter((a) => a.label !== 'Audio').map((a) => (
                <div key={a.media_id} className="cams-row">
                  <select className="cams-letter" value={a.label.replace(/\d+$/, '')} disabled={busy === a.media_id} onChange={(e) => setLabel(t, a.media_id, e.target.value)} aria-label="Which camera">
                    {'ABCDEFGH'.split('').map((l) => <option key={l} value={l}>CAM {l}</option>)}
                  </select>
                  <span className="grow ellipsis">{media.get(a.media_id)?.name || 'missing file'}</span>
                  <span className={cls('muted small', a.synced === 'sound' && 'ok')}>{SYNC_TEXT[a.synced || ''] || (t.angles.length > 1 ? 'lined up by timecode' : 'only camera')}</span>
                </div>
              ))}
              <div className="cams-row sub muted small">
                <span className="cams-letter-pad">Sound</span>
                <span className="grow ellipsis">{audio ? audio.name : 'No sound, so nothing to transcribe'}</span>
              </div>
              {t.angles.filter((a) => a.label !== 'Audio').length === 1 && t.kind === 'sync' && (
                <div className="cams-hint small">One camera. Have another angle of this? Add its file and it joins this interview on its own, lined up by timecode and sound.</div>
              )}
            </div>
          )
        })}
        {other.length > 0 && <div className="muted small pad-x">Plus {plural(other.length, 'clip')} with no dialogue (b-roll), not transcribed.</div>}
        {unplaced.length > 0 && <div className="muted small pad-x">{plural(unplaced.length, 'file')} still being read: {unplaced.slice(0, 3).map((m) => m.name).join(', ')}{unplaced.length > 3 ? '…' : ''}</div>}
      </div>
      <div className="form-actions">
        <button className="btn btn-ghost" onClick={onAdd}>Add footage</button>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  )
}

/** The plain-words version of how to add footage, shown in the Add footage dialog. */
export function HowItWorks() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('osel-howto') !== 'closed' } catch { return true }
  })
  const toggle = () => setOpen((o) => {
    try { localStorage.setItem('osel-howto', o ? 'closed' : 'open') } catch {}
    return !o
  })
  return (
    <div className="explain small">
      <button className="explain-head" onClick={toggle} aria-expanded={open}>
        <b>What to add, and what happens next</b><span className="muted">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <>
          <ol>
            <li><b>Add every camera's files, plus the sound recorder's files if you have them.</b> Several clips per camera (stops and starts) are fine.</li>
            <li><b>We sync it for you.</b> Clips rolling at the same time become one interview: placed by timecode, then lined up exactly by matching the sound waveforms. Cameras with wrong or missing timecode still land on the right frame if their sound matches.</li>
            <li><b>Name tells us the camera.</b> Put the letter in the file or folder name: A001_C003.mov, Interview_CamB.mp4, a "B cam" folder. Otherwise we guess and you can fix it under Cameras & sound.</li>
            <li><b>Exported from an edit?</b> Export one file per camera from the same timeline (full length, same start), not a cut that switches between them.</li>
            <li><b>Then it runs on its own:</b> reading the files, lining up, transcribing, and making a web version of each video. The progress chip at the top shows each step with time left. The transcript is usually ready before the video.</li>
          </ol>
        </>
      )}
    </div>
  )
}

/** What each camera's clips say they were shot in, in a line: "Sony S-Log3 S-Gamut3.Cine · ILME-FX3". */
function readOut(clips: Media[]) {
  // A read that failed is tried again later (worker/look.ts failedRead): say so, rather than "reading" for minutes.
  const unread = clips.filter((m) => m.profile_at == null)
  if (unread.length && unread.every((m) => /^Could not read this file \(try /.test(m.profile_note || ''))) return { auto: null as string | null, text: 'Could not read this file (trying again)' }
  if (unread.length) return { auto: null as string | null, text: 'reading camera info…' }
  const keys = [...new Set(clips.map((m) => m.profile || ''))]
  const logs = keys.filter((k) => k && k !== 'none')
  const note = clips.find((m) => m.profile && m.profile !== 'none')?.profile_note || clips[0]?.profile_note || ''
  if (logs.length === 1 && keys.length === 1) return { auto: logs[0], text: note ? `${note}` : profileLabel(logs[0])! }
  if (logs.length) return { auto: logs[0], text: `mixed: ${logs.map(profileLabel).join(', ')}${keys.length > logs.length ? ', some normal video' : ''}` }
  if (keys.includes('none')) return { auto: null, text: `normal video${note ? ` (${note})` : ''}` }
  // Nothing about the camera: say why (no camera info, or the file couldn't be read).
  return { auto: null, text: clips.find((m) => m.profile_note)?.profile_note || 'the files don\'t say' }
}

/** The Osmo look, team side: what each camera was shot in, and whether clients pick the look. */
function LookSection({ data, media, setLook }: { data: ProjectData; media: Map<string, Media>; setLook: (change: { look?: string; client?: boolean; cams?: Record<string, string> }) => Promise<unknown> }) {
  const cams = lookCams(data.project)
  const letters = new Map<string, Media[]>()
  for (const t of data.takes) for (const a of t.angles) {
    if (a.label === 'Audio') continue
    const l = a.label.replace(/\d+$/, ''), m = media.get(a.media_id)
    if (!m || m.kind !== 'video') continue
    if (!letters.has(l)) letters.set(l, [])
    if (!letters.get(l)!.includes(m)) letters.get(l)!.push(m)
  }
  // Shows at once (the store is optimistic); a failed save puts it back and says so.
  const change = (c: { look?: string; client?: boolean; cams?: Record<string, string> }) =>
    setLook(c).catch((e) => toast(e instanceof ApiError ? e.message : 'Could not change the look', 'err'))
  const on = !!data.project.look
  return (
    <div className="look-box">
      <div className="look-head">
        <b>Look</b>
        <span className="muted small">
          {on ? `${lookLabel(data.project.look)} on log cameras.` : 'As shot.'} Pick it under the player. Log footage plays through a Film Emulation look for its camera, for everyone on this project. Color only, the files aren't touched.
        </span>
      </div>
      <label className="look-switch">
        <input type="checkbox" checked={!!data.project.look_client} onChange={(e) => change({ client: e.target.checked })} />
        <span>The client can pick the look</span>
      </label>
      {letters.size > 0 && (
        <div className="look-cams">
          {[...letters.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([l, clips]) => {
            const r = readOut(clips)
            const pick = cams[l] || 'auto'
            const now = pick === 'auto' ? r.auto : pick === 'none' ? null : pick
            return (
              <div key={l} className="cams-row">
                <span className="cams-letter-pad mono">CAM {l}</span>
                <span className="grow ellipsis muted small" title={r.text}>{r.text}</span>
                <select className="look-pick" value={pick} aria-label={`Camera ${l} profile`} onChange={(e) => change({ cams: { [l]: e.target.value } })}>
                  <option value="auto">{r.auto ? `Auto: ${profileLabel(r.auto)}` : 'Auto: as shot'}</option>
                  {PROFILES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  <option value="none">Not log, show as shot</option>
                </select>
                <span className={cls('look-dot', on && now && 'on')} title={on && now ? 'Plays with the look' : 'Plays as shot'} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
